import { useMemo } from 'react'
import { isNil, times } from 'lodash'
import { DateTime } from 'luxon'

import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { CreateUserIncomePlanSource } from '@/features/income-planning/incomePlanning.slice'
import {
  addCurrencyArray,
  centsToCurrency,
  dollarsToCurrency,
} from '@/utils/currencyHelpers'

export const useSelectedMonthOptions = () => {
  const currentDate = useMemo(() => DateTime.now(), [])

  return useMemo(
    () =>
      // Flat map used so months not separated out by year
      times(2).flatMap((yearOffset) => {
        const year = currentDate.year + yearOffset

        return times(12).map((iMonth) => {
          const month = iMonth + 1
          return {
            value: `${year}-${month}`,
            text: DateTime.fromObject({ month, year }).toFormat(
              DATE_FORMATS_LUXON.MONTH_YEAR_LONG
            ),
          }
        })
      }),
    [currentDate.year]
  )
}

const getTotalIncome = (
  sources: Array<CreateUserIncomePlanSource & { rate?: number }>
) =>
  dollarsToCurrency(
    addCurrencyArray(
      sources.map((s) => {
        const base = s.rate
          ? dollarsToCurrency(s.rate)
          : centsToCurrency(s.rateInCents)

        if (s.cadence === 'monthly') {
          return base
        } else {
          return base.multiply(s.numberPerMonth || 0)
        }
      })
    )
  )

export const getPlanTotals = (
  sources: Array<CreateUserIncomePlanSource & { rate?: number }>
) => {
  const therapySources = sources.filter((s) => s.source === 'therapy')
  const nonTherapySources = sources.filter((s) => s.source === 'non_therapy')

  const sessionsPerMonth = therapySources.reduce(
    (i, s) => i + (isNil(s.numberPerMonth) ? 0 : Number(s.numberPerMonth)),
    0
  )

  const ratePerSession = dollarsToCurrency(
    addCurrencyArray(
      therapySources.map((s) =>
        (isNil(s.rate)
          ? centsToCurrency(s.rateInCents)
          : dollarsToCurrency(s.rate)
        ).multiply(s.numberPerMonth || 0)
      )
    )
  ).divide(sessionsPerMonth)

  const therapyIncome = getTotalIncome(therapySources)
  const nonTherapyIncome = getTotalIncome(nonTherapySources)

  return {
    totalPlannedIncomeInCents: therapyIncome.add(nonTherapyIncome).intValue,
    therapyIncomeInCents: therapyIncome.intValue,
    nonTherapyIncomeInCents: nonTherapySources.length
      ? nonTherapyIncome.intValue
      : null,
    ratePerSession: ratePerSession.intValue,
    sessionsPerMonth,
  }
}

export const getNameForCopy = (
  val: string,
  sources: CreateUserIncomePlanSource[]
) => {
  const mappedNames = sources.map((s) => s.name)

  // Find if there is a number at the end of the name
  const endNumberSt = val.match(/\d+$/g)
  let endNumber = endNumberSt ? Number(endNumberSt[0]) : 1

  // If there isn't a current number set the initial number as 1
  let newName = endNumberSt ? val : `${val} 1`

  do {
    // Increment number in name until there aren't any sources with that name
    newName = newName.replace(/\d+$/g, (++endNumber).toString())
  } while (mappedNames.includes(newName))

  return newName
}
